import React, { useEffect, useRef, useCallback } from "react";
import { Box, useColorMode } from "@chakra-ui/react";

const SCANNER_ORIGIN = process.env.NODE_ENV === "development" 
  ? "http://localhost:5173" 
  : "https://scanner.wealthbee.io";

export const Scanner = () => {
  const { colorMode } = useColorMode();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Function to send theme to iframe
  const sendThemeToIframe = useCallback(() => {
    if (iframeRef.current?.contentWindow) {
      const theme = colorMode === "dark" ? "dark" : "light";
      iframeRef.current.contentWindow.postMessage(
        { type: "theme-change", theme },
        SCANNER_ORIGIN
      );
    }
  }, [colorMode]);

  // Function to send token to iframe
  const sendTokenToIframe = useCallback(() => {
    if (iframeRef.current?.contentWindow) {
      const token = localStorage.getItem("token");
      if (token) {
        iframeRef.current.contentWindow.postMessage(
          { type: "scanner-auth-token", token },
          SCANNER_ORIGIN
        );
      }
    }
  }, []);

  // Function to notify iframe that token was removed
  const notifyTokenRemoved = useCallback(() => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        { type: "scanner-auth-token-removed" },
        SCANNER_ORIGIN
      );
    }
  }, []);

  // Handle messages from iframe (token requests)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only accept messages from the scanner origin
      if (event.origin !== SCANNER_ORIGIN) {
        return;
      }

      // Handle token request from iframe
      if (event.data?.type === "scanner-auth-request") {
        sendTokenToIframe();
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [sendTokenToIframe]);

  // Monitor token changes in localStorage
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "token") {
        if (e.newValue) {
          // Token was added or updated
          sendTokenToIframe();
        } else {
          // Token was removed
          notifyTokenRemoved();
        }
      }
    };

    // Listen for storage events (from other tabs/windows)
    window.addEventListener("storage", handleStorageChange);

    // Also monitor localStorage directly (for same-tab changes)
    const originalSetItem = localStorage.setItem;
    const originalRemoveItem = localStorage.removeItem;

    localStorage.setItem = function (key: string, value: string) {
      originalSetItem.apply(this, [key, value]);
      if (key === "token") {
        sendTokenToIframe();
      }
    };

    localStorage.removeItem = function (key: string) {
      originalRemoveItem.apply(this, [key]);
      if (key === "token") {
        notifyTokenRemoved();
      }
    };

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      localStorage.setItem = originalSetItem;
      localStorage.removeItem = originalRemoveItem;
    };
  }, [sendTokenToIframe, notifyTokenRemoved]);

  // Update iframe theme when color mode changes
  useEffect(() => {
    // Small delay to ensure iframe is ready
    const timer = setTimeout(() => {
      sendThemeToIframe();
    }, 100);

    return () => clearTimeout(timer);
  }, [colorMode, sendThemeToIframe]);

  // Handle iframe load event
  const handleIframeLoad = useCallback(() => {
    // Send theme and token once iframe is loaded
    sendThemeToIframe();
    sendTokenToIframe();
  }, [sendThemeToIframe, sendTokenToIframe]);

  // Build URL with theme parameter
  const iframeSrc = `${SCANNER_ORIGIN}/scanner?theme=${colorMode}`;

  return (
    <Box
      w="full"
      h="calc(100vh - 16px)"
      overflow="hidden"
      position="relative"
    >
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        width="100%"
        height="100%"
        frameBorder={0}
        onLoad={handleIframeLoad}
        style={{
          border: "none",
          display: "block",
          "padding-right": "10px",
        }}
        allowFullScreen
        title="Scanner"
      />
    </Box>
  );
};

